import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Phonelink,
  AdsClick,
  Insights,
  CallToAction,
  ChatBubbleOutline,
  SmartToy,
  RecordVoiceOver,
  LocalPhone,
  PhoneInTalk,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const HEADING_FONT = "'Manrope', sans-serif";
const BODY_FONT = "'Inter', sans-serif";

// Same icons as ProductDisplay.jsx for consistency
const ICON_MAP = {
  'business-phone': Phonelink,
  'fmc': Phonelink,
  'call-tracking': AdsClick,
  'ai-conversation': Insights,
  'callback': CallToAction,
  'omnichannel-chat': ChatBubbleOutline,
  'eniq-ai': SmartToy,
  'voicebot': RecordVoiceOver,
  'phone-numbers': LocalPhone,
  'voice-traffic': PhoneInTalk,
};

export default function ProductCard({ product, currency = '$', formatPrice }) {
  const { t } = useTranslation();
  const { productType, label, price, description } = product;

  const IconComponent = ICON_MAP[productType] || PhoneInTalk;
  const isIncluded = price === 0;

  // Split label: "FMC Basic (6 користувачів)" → name="FMC Basic", quantity="6 користувачів"
  const bracketMatch = label?.match(/^(.+?)\s*\((.+)\)$/);
  const productName = bracketMatch ? bracketMatch[1] : label;
  // For eniq-ai/voicebot: hide bracket quantity — their description has full plan details
  const hasRichDescription = (productType === 'eniq-ai' || productType === 'voicebot') && description;
  const quantity = bracketMatch && !hasRichDescription ? bracketMatch[2] : null;

  // Don't show description if it duplicates quantity info
  const showDescription = description && !label?.includes(description) && description !== quantity;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: '1px solid rgba(187,203,184,0.12)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 10px 40px rgba(0,0,0,0.08)',
        },
      }}
    >
      {/* Top row: icon + Included badge */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: '#c6e7ff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconComponent sx={{ color: '#00658d', fontSize: 24 }} />
        </Box>
      </Box>

      {/* Middle: name + description */}
      <Typography
        sx={{
          fontFamily: HEADING_FONT,
          fontWeight: 700,
          fontSize: '1.125rem',
          mb: 0.5,
        }}
      >
        {productName}
      </Typography>
      {quantity && !isIncluded && (
        <Typography
          sx={{
            fontFamily: BODY_FONT,
            color: '#3c4a3c',
            fontSize: '0.8125rem',
            mb: 1,
          }}
        >
          {quantity}
        </Typography>
      )}
      {showDescription && (
        <Typography
          variant="body2"
          sx={{
            fontFamily: BODY_FONT,
            color: '#3c4a3c',
            fontSize: '0.875rem',
            mb: 2,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      )}

      {/* Bottom: price */}
      <Box sx={{ mt: 'auto', pt: 1 }}>
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontWeight: 700,
            fontSize: '1.25rem',
            color: '#006e2a',
          }}
        >
          {isIncluded
            ? t('offer.included', 'Included')
            : (formatPrice ? formatPrice(product) : `${currency}${Math.ceil(price)}`)}
        </Typography>
      </Box>
    </Paper>
  );
}
